import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConnectionRequirements = ({ isConnected, walletData }) => {
  const [balanceStatus, setBalanceStatus] = useState('checking');
  const [gasEstimate, setGasEstimate] = useState(null);
  const [requirements, setRequirements] = useState([]);

  useEffect(() => {
    if (isConnected && walletData) {
      checkRequirements();
    } else {
      resetRequirements();
    }
  }, [isConnected, walletData]);

  const checkRequirements = async () => {
    // Simulate checking requirements
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const balance = parseFloat(walletData?.balance || '0');
    const minBalance = 0.1; // Minimum MATIC required
    
    setBalanceStatus(balance >= minBalance ? 'sufficient' : 'insufficient');
    setGasEstimate({
      notarization: '0.005',
      usd: '0.12'
    });

    setRequirements([
      {
        id: 'wallet',
        title: 'Wallet Connected',
        description: 'Web3 wallet successfully connected',
        status: 'completed',
        icon: 'CheckCircle'
      },
      {
        id: 'network',
        title: 'Network Compatibility',
        description: 'Connected to supported Polygon network',
        status: 'completed',
        icon: 'CheckCircle'
      },
      {
        id: 'balance',
        title: 'Sufficient Balance',
        description: `Minimum ${minBalance} MATIC required for transactions`,
        status: balance >= minBalance ? 'completed' : 'warning',
        icon: balance >= minBalance ? 'CheckCircle' : 'AlertTriangle',
        details: `Current: ${balance} MATIC`
      },
      {
        id: 'gas',
        title: 'Gas Fee Estimation',
        description: 'Estimated cost for notarization transaction',
        status: 'info',
        icon: 'Fuel',
        details: `~${gasEstimate?.notarization} MATIC ($${gasEstimate?.usd})`
      }
    ]);
  };

  const resetRequirements = () => {
    setBalanceStatus('checking');
    setGasEstimate(null);
    setRequirements([
      {
        id: 'wallet',
        title: 'Wallet Connection',
        description: 'Connect a Web3 wallet to continue',
        status: 'pending',
        icon: 'Circle'
      },
      {
        id: 'network',
        title: 'Network Setup',
        description: 'Switch to a supported network',
        status: 'pending',
        icon: 'Circle'
      },
      {
        id: 'balance',
        title: 'Balance Check',
        description: 'Verify sufficient funds for transactions',
        status: 'pending',
        icon: 'Circle'
      }
    ]);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-destructive';
      case 'info':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 border-success/20';
      case 'warning':
        return 'bg-warning/10 border-warning/20';
      case 'error':
        return 'bg-destructive/10 border-destructive/20';
      case 'info':
        return 'bg-primary/10 border-primary/20';
      default:
        return 'bg-muted/50 border-border';
    }
  };

  const handleAddFunds = () => {
    // Open a guide or exchange link
    window.open('https://wallet.polygon.technology/bridge', '_blank');
  };

  const allRequirementsMet = requirements?.every(req => 
    req?.status === 'completed' || req?.status === 'info'
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Connection Requirements</h3>
        {allRequirementsMet && isConnected && (
          <div className="flex items-center space-x-2 px-3 py-1 bg-success/10 text-success rounded-full">
            <Icon name="CheckCircle" size={14} />
            <span className="text-sm font-medium">Ready</span>
          </div>
        )}
      </div>
      <div className="space-y-4">
        {requirements?.map((requirement) => (
          <div
            key={requirement?.id}
            className={`p-4 border rounded-lg ${getStatusBg(requirement?.status)}`}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <Icon 
                  name={requirement?.icon} 
                  size={18} 
                  className={getStatusColor(requirement?.status)} 
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-foreground">
                  {requirement?.title}
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {requirement?.description}
                </p>
                
                {requirement?.details && (
                  <p className={`text-xs mt-2 font-mono ${getStatusColor(requirement?.status)}`}>
                    {requirement?.details}
                  </p>
                )}
              </div>

              {requirement?.status === 'warning' && requirement?.id === 'balance' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddFunds}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Funds
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {isConnected && walletData && (
        <div className="mt-6 pt-4 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Balance */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Current Balance</span>
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">
                    {walletData?.balance} MATIC
                  </p>
                  <p className="text-xs text-muted-foreground">
                    ≈ ${(parseFloat(walletData?.balance) * 0.85)?.toFixed(2)} USD
                  </p>
                </div>
              </div>
            </div>

            {/* Estimated Costs */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Est. Transaction Cost</span>
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">
                    {gasEstimate?.notarization || '~0.005'} MATIC
                  </p>
                  <p className="text-xs text-muted-foreground">
                    ≈ ${gasEstimate?.usd || '0.12'} USD
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Lightbulb" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">Getting Started Tips</p>
            <ul className="text-sm text-muted-foreground mt-2 space-y-1">
              <li>• Keep at least 0.1 MATIC for multiple transactions</li>
              <li>• Gas fees are typically lower during off-peak hours</li>
              <li>• Your wallet will prompt you to confirm each transaction</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionRequirements;